/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import MultiSelectString from 'components/common/MultiSelectString';
import Open5eDocument from "model/formats/Open5eDocument";

interface PropertySetDoubleProps<T extends string | Open5eDocument> {
  name: string;
  values1: string | string[] | Record<string, any> | null;
  values2: string | string[] | Record<string, any> | null;
  values1Array?: T[];
  values2Array?: T[];
  isOpen1: boolean;
  isOpen2: boolean;
  setIsOpen1: (isOpen: boolean) => void;
  setIsOpen2: (isOpen: boolean) => void;
  options1?: T[];
  options2?: T[];
  allowCustom1?: boolean;
  allowCustom2?: boolean;
  showClear1?: boolean;
  showClear2?: boolean;
  onSave1?: (selectedValues: string) => void;
  onSave2?: (selectedValues: string) => void;
  onSaveArray1?: (selectedValuesArray: T[]) => void;
  onSaveArray2?: (selectedValuesArray: T[]) => void;
  alwaysShow?: boolean;
  columns1?: number;
  columns2?: number;
  showGrouping?: boolean;
  className?: string;
  maxSelections1?: number;
  maxSelections2?: number;
  displayFunction?: (option: T) => string | JSX.Element;
  getIdFunction?: (option: T) => string;
  filterFunction?: (option: T) => boolean;
  onClickOutside: (event: MouseEvent) => void;
}

const PropertySetDouble = <T extends string | Open5eDocument>({
  name,
  values1,
  values2,
  values1Array,
  values2Array,
  isOpen1,
  isOpen2,
  setIsOpen1,
  setIsOpen2,
  options1,
  options2,
  allowCustom1 = false,
  allowCustom2 = false,
  showClear1 = false,
  showClear2 = false,
  onSave1,
  onSave2,
  onSaveArray1,
  onSaveArray2,
  alwaysShow = false,
  columns1 = 2,
  columns2 = 2,
  showGrouping = true,
  className = '',
  maxSelections1,
  maxSelections2,
  displayFunction,
  getIdFunction,
  filterFunction,
  onClickOutside
}: PropertySetDoubleProps<T>) => {

  const getCont = (values: string | string[] | Record<string, any> | null, valuesArray: T[] | undefined): React.ReactNode => {
    let content: React.ReactNode = '';
    if (valuesArray && displayFunction) {
      content = valuesArray.map((o, index) => (
        <React.Fragment key={index}>
          {displayFunction(o)}
          {index < valuesArray.length - 1 && ', '}
        </React.Fragment>
      ));
    } else if (Array.isArray(values)) {
      // Handle array of values
      content = values.filter(Boolean).join(', ');
    } else if (typeof values === 'object' && values !== null) {
      // Handle object of values
      const entries = Object.entries(values)
                            .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`);
      content = entries.join(', ');
    } else if (typeof values === 'string') {
      // Handle string values directly
      content = values;
    }
    return content;
  }
  let content1: React.ReactNode = getCont(values1, values1Array);
  let content2: React.ReactNode = getCont(values2, values2Array);

  // Check if content is empty (for string or empty array/object)
  if (!content1 && !values1Array && !content2 && !values2Array && !alwaysShow) return null;

  const getContent = (content: string,
                      valuesArray: (T[] | undefined),
                      options: T[],
                      isOpen: boolean,
                      setIsOpen: (isOpen: boolean) => void,
                      allowCustom: boolean,
                      showClear: boolean,
                      onSave: ((selectedValues: string) => void) | undefined,
                      onSaveArray: ((selectedValuesArray: T[]) => void) | undefined,
                      columns: number,
                      maxSelections: number | undefined) => {
    return (<MultiSelectString
        values={content}
        valuesArray={valuesArray}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        options={options}
        allowCustom={allowCustom}
        showClear={showClear}
        onSave={onSave}
        onSaveArray={onSaveArray}
        columns={columns}
        className={className}
        showGrouping={showGrouping}
        maxSelections={maxSelections}
        displayFunction={displayFunction}
        getIdFunction={getIdFunction}
        filterFunction={filterFunction}
        onClickOutside={onClickOutside}
      />);
  }

  if (content1 || values1Array || alwaysShow) {
    if (options1) {
      content1 = getContent(content1 as string, values1Array, options1, isOpen1, setIsOpen1, allowCustom1, showClear1, onSave1, onSaveArray1, columns1, maxSelections1);
    }
  }
  if (content2 || values2Array || alwaysShow) {
    if (options2) {
      content2 = getContent(content2 as string, values2Array, options2, isOpen2, setIsOpen2, allowCustom2, showClear2, onSave2, onSaveArray2, columns2, maxSelections2);
    }
  }

  return (
    <div className={`property-line ${className}`}>
      {name && <h4>{name}</h4>}
      {content1 && <span>{content1}{content2 && ','}</span>}
      <span>{content2}</span>
    </div>
  );
};

export default PropertySetDouble;
